import React, { useState, useEffect, Suspense, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { type Event } from '../types';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';

const EventsPageContent: React.FC = () => {
    const { t } = useTranslation();
    const { showNotification, showConfirmation } = useNotification();
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        api.get('/Events/my-events')
            .then(response => setEvents(response.data))
            .catch(err => {
                showNotification('Nie udało się pobrać wydarzeń.', 'error');
                console.error(err);
            })
            .finally(() => setLoading(false));
    }, [showNotification]);

    const handleDeleteEvent = async (eventId: string, eventName: string) => {
        showConfirmation(`${t('eventsList.deleteConfirm')}: "${eventName}"?`, async () => {
            try {
                await api.delete(`/Events/${eventId}`);
                setEvents(currentEvents => currentEvents.filter(event => event.id !== eventId));
                showNotification(t('eventsList.deleteSuccess'), 'success');
            } catch (err: any) {
                const errorMessage = err.response?.data || t('eventsList.deleteError');
                showNotification(errorMessage, 'error');
            }
        });
    };

    const handleToggleFavorite = async (eventId: string) => {
        setEvents(currentEvents =>
            currentEvents.map(event =>
                event.id === eventId ? { ...event, isFavorite: !event.isFavorite } : event
            )
        );
        try {
            await api.post(`/Events/${eventId}/toggle-favorite`);
        } catch (error) {
            showNotification('Błąd podczas zmiany statusu ulubionych.', 'error');
            console.error(error);
            setEvents(currentEvents =>
                currentEvents.map(event =>
                    event.id === eventId ? { ...event, isFavorite: !event.isFavorite } : event
                )
            );
        }
    };

    const handleTogglePublic = async (eventId: string) => {
        setEvents(currentEvents =>
            currentEvents.map(event =>
                event.id === eventId ? { ...event, isPublic: !event.isPublic } : event
            )
        );
        try {
            await api.post(`/Events/${eventId}/toggle-public`);
        } catch (error) {
            showNotification('Błąd podczas zmiany statusu publicznego.', 'error');
            console.error(error);
            setEvents(currentEvents =>
                currentEvents.map(event =>
                    event.id === eventId ? { ...event, isPublic: !event.isPublic } : event
                )
            );
        }
    };

    // --- Helpers ---
    const norm = (s?: string) => (s || '').toString().trim().toLowerCase();
    const sportLabel = (discipline?: string) => {
        const key = norm(discipline);
        if (!key) return t('common.unknown') || 'Unknown';
        const i18nKey = `sportTypes.${key}` as const;
        const translated = t(i18nKey);
        if (!translated || translated === i18nKey) {
            const raw = discipline || '';
            return raw.charAt(0).toUpperCase() + raw.slice(1);
        }
        return translated;
    };

    const pickScore = (e: Event) => {
        const a = (e as any).scoreA ?? null;
        const b = (e as any).scoreB ?? null;
        return (a ?? null) !== null && (b ?? null) !== null ? { a: Number(a), b: Number(b) } : null;
    };

    const renderScoreBadge = (e: Event) => {
        if (e.status !== 'InProgress' && e.status !== 'Finished') return null;
        const score = pickScore(e);
        if (!score) return null;

        const badgeClasses =
            e.status === 'InProgress'
                ? 'bg-yellow-500/20 text-yellow-300'
                : 'bg-green-500/20 text-green-300';

        const label =
            e.status === 'InProgress'
                ? (t('eventsList.inProgressScore') || 'Wynik (LIVE)')
                : (t('eventsList.finalScore') || 'Ostateczny wynik');

        // mini-badge LIVE/FIN
        const tiny =
            e.status === 'InProgress'
                ? <span className="ml-2 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-600/40 text-yellow-200 align-middle">LIVE</span>
                : <span className="ml-2 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-600/40 text-green-100 align-middle">FIN</span>;

        return (
            <div
                className={`mt-3 inline-flex items-center rounded-lg px-3 py-1 text-sm font-semibold ${badgeClasses}`}
                title={label}
            >
                <span className="mr-2">{label}:</span>
                <span className="tabular-nums">{score.a} : {score.b}</span>
                {tiny}
            </div>
        );
    };

    // --- Grupowanie i sortowanie: dyscyplina → a w środku po dacie+godzinie rosnąco ---
    const grouped = useMemo(() => {
        const map = new Map<string, Event[]>();
        for (const e of events) {
            const key = norm((e as any).sportType ?? (e as any).discipline ?? '');
            const bucket = map.get(key) || [];
            bucket.push(e);
            map.set(key, bucket);
        }
        // sortuj każdy koszyk po startTime rosnąco
        Array.from(map.keys()).forEach(k => {
            const arr = map.get(k) as Event[];
            arr.sort((a: Event, b: Event) =>
                new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
            );
            map.set(k, arr);
        });
        return map;
    }, [events]);

    const orderedKeys = useMemo(() => {
        const keys = Array.from(grouped.keys());
        return keys.sort((a, b) => sportLabel(a).localeCompare(sportLabel(b)));
    }, [grouped]);

    if (loading) return <p className="text-white text-center">{t('common.loading')}</p>;

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-white">{t('eventsList.pageTitle')}</h1>
                <Link
                    to="/create-event"
                    className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                >
                    + {t('eventsList.createNewEvent')}
                </Link>
            </div>

            {events.length === 0 ? (
                <div className="bg-gray-800 rounded-lg p-8 text-center text-gray-400">
                    <p>{t('eventsList.noEventsYet')}</p>
                </div>
            ) : (
                <div className="space-y-10">
                    {orderedKeys.map(key => {
                        const list = grouped.get(key) || [];
                        if (list.length === 0) return null;

                        return (
                            <section key={key}>
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-2xl font-bold text-white">
                                        {sportLabel(key)}
                                        <span className="ml-3 text-sm px-2 py-1 rounded-full bg-gray-800 text-gray-300 align-middle">
                                            {list.length}
                                        </span>
                                    </h2>
                                </div>

                                {/* Pojedyncza kolumna: pełna szerokość */}
                                <div className="flex flex-col gap-6">
                                    {list.map(event => (
                                        <div
                                            key={event.id}
                                            className="w-full bg-gray-800 rounded-xl shadow-lg p-6 flex flex-col"
                                        >
                                            {/* Nagłówek + ikony */}
                                            <div className="flex justify-between items-start gap-3">
                                                <Link to={`/event/${event.id}`} className="flex-grow">
                                                    <h3 className="text-2xl font-bold text-white hover:text-blue-400 transition-colors break-words">
                                                        {event.eventName}
                                                    </h3>
                                                </Link>
                                                {/* Ikony: edycja, ulubione, publiczne (z przerwami) */}
                                                <div className="flex items-center gap-3">
                                                    <Link
                                                        to={`/events/edit/${event.id}`}
                                                        title={t('common.edit') || 'Edytuj'}
                                                        className="text-gray-300 hover:text-white text-xl leading-none"
                                                    >
                                                        ✎
                                                    </Link>
                                                    <button
                                                        onClick={() => handleToggleFavorite(event.id)}
                                                        className="text-2xl leading-none"
                                                        title="Ulubione"
                                                    >
                                                        {event.isFavorite ? '⭐' : '☆'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleTogglePublic(event.id)}
                                                        className="text-2xl leading-none"
                                                        title="Publiczne"
                                                    >
                                                        {event.isPublic ? '🌍' : '🔒'}
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Zespoły */}
                                            <p className="text-gray-300 mt-2">
                                                {event.teamAName} vs {event.teamBName}
                                            </p>

                                            {/* Data + godzina */}
                                            <div className="mt-3 flex items-center text-gray-300">
                                                <span className="mr-2">📅</span>
                                                <span>
                                                    {new Date(event.startTime).toLocaleDateString('pl-PL')} •{' '}
                                                    {new Date(event.startTime).toLocaleTimeString('pl-PL', {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </div>

                                            {/* Wynik / badge LIVE/FIN */}
                                            {renderScoreBadge(event)}

                                            <hr className="border-gray-700 my-4" />

                                            {/* Stopka */}
                                            <div className="flex justify-between items-center">
                                                <span className="text-sm text-gray-400">
                                                    {t('eventsList.status')}: {t(`statuses.${event.status}` as const)}
                                                </span>
                                                <button
                                                    onClick={(e) => { e.preventDefault(); handleDeleteEvent(event.id, event.eventName); }}
                                                    disabled={event.status === 'InProgress'}
                                                    className="text-sm text-red-400 hover:text-red-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                                                >
                                                    {t('eventsList.delete')}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const EventsPage = () => (
    <Suspense fallback={<div className="text-center p-8 text-white">Ładowanie...</div>}>
        <EventsPageContent />
    </Suspense>
);

export default EventsPage;
